import { z } from "zod";

import {
  AddressSchema,
  IsoTimestampSchema,
  LocalizedTextSchema,
  MoneyHalalasSchema,
  PhoneSchema,
} from "./common";

/** docs/03 — `return_order.source_channel`. */
export const SourceChannelSchema = z.enum([
  "salla",
  "zid",
  "shopify",
  "manual",
  "rto",
]);
export type SourceChannel = z.infer<typeof SourceChannelSchema>;

/**
 * Store order status, as far as THIS system cares.
 *
 * The docs enumerate no store-order status vocabulary; policy check 2 draws exactly one
 * distinction — "delivered only" (docs/05) — and the fixture corpus needs one
 * non-delivered order (docs/09). Modelling only that distinction keeps us from inventing
 * a per-platform status list that Salla, Zid and Shopify each disagree about; the adapter
 * normalises into this. Widen it only when a real platform mapping arrives.
 */
export const OrderStatusSchema = z.enum(["delivered", "not_delivered"]);
export type OrderStatus = z.infer<typeof OrderStatusSchema>;

export const OrderCustomerSchema = z.object({
  name: z.string().min(1),
  phone: PhoneSchema,
  address: AddressSchema,
});
export type OrderCustomer = z.infer<typeof OrderCustomerSchema>;

/** A line of the original outbound order, as returned by the adapter. */
export const SirdabOrderLineSchema = z.object({
  lineRef: z.string().min(1),
  externalProductRef: z.string().min(1),
  variant: z.string().nullable(),
  productName: LocalizedTextSchema,
  qty: z.number().int().positive(),
  /** Integer halalas. */
  unitPrice: MoneyHalalasSchema,
  /** Integer halalas. Carried for credit-note-ready data only — we never issue the document (ADR-004). */
  vatAmount: MoneyHalalasSchema,
  sirdabSkuId: z.string().nullable().optional(),
  /**
   * Per-line delivery instant, for split shipments where one line arrived weeks before
   * another (fixtures/orders.json ORD-48220). Absent means "same as the order's
   * `deliveredAt`"; the window check must read this first and fall back to the header.
   */
  deliveredAt: IsoTimestampSchema.nullable().optional(),
});
export type SirdabOrderLine = z.infer<typeof SirdabOrderLineSchema>;

/**
 * The order a lookup resolves to.
 * Fixtures store `deliveredDaysAgo` against an injectable clock; `deliveredAt` is the
 * resolved ISO form used in transport.
 */
export const SirdabOrderSchema = z.object({
  merchantId: z.string().min(1),
  sourceChannel: SourceChannelSchema,
  orderRef: z.string().min(1),
  status: OrderStatusSchema,
  deliveredAt: IsoTimestampSchema.nullable(),
  customer: OrderCustomerSchema,
  /** Original invoice reference. Passed through on `return.resolved` for the merchant's credit note. */
  invoiceRef: z.string().nullable().optional(),
  lines: z.array(SirdabOrderLineSchema),
});
export type SirdabOrder = z.infer<typeof SirdabOrderSchema>;

/**
 * What the shopper portal may see. Deliberately narrower than `SirdabOrder`:
 * no internal ids, no other orders, no raw store payload (docs/06).
 */
export const PublicOrderLineSchema = z.object({
  lineRef: z.string().min(1),
  productName: LocalizedTextSchema,
  variant: z.string().nullable(),
  qty: z.number().int().positive(),
  unitPrice: MoneyHalalasSchema,
  /**
   * docs/08 step 5: "if the replacement variant is out of stock, do not offer [exchange]
   * for that line". Populated by the API from a stock check on the resolved
   * SKU (`available > 0`).
   *
   * A BOOLEAN, not a quantity, and deliberately not a SKU: the shopper surface must not
   * leak internal SKU identifiers or Sirdab's inventory levels, and "can I swap this" is
   * the only question the screen asks. `PublicOrderLine` stays free of
   * `sirdabSkuId` for the same reason `PublicOrder` carries no merchant id.
   *
   * OPTIONAL, and absent means UNKNOWN, which must be read as "offer exchange". Suppressing
   * an option we cannot substantiate would be the portal inventing a policy decision, which
   * it is not allowed to do — the merchant's `resolutions.exchange` toggle is the policy,
   * and this is only a stock fact that can further suppress it. Absent arises legitimately:
   * an unmapped SKU, an adapter that could not answer, or an API build that does not yet
   * populate the field. The submission is re-validated server-side either way (docs/05,
   * "Two entry points, not one").
   */
  replacementInStock: z.boolean().optional(),
});
export type PublicOrderLine = z.infer<typeof PublicOrderLineSchema>;

export const PublicOrderSchema = z.object({
  orderRef: z.string().min(1),
  deliveredAt: IsoTimestampSchema.nullable(),
  lines: z.array(PublicOrderLineSchema),
});
export type PublicOrder = z.infer<typeof PublicOrderSchema>;

/**
 * One row of "your orders" for a shopper who has proven a phone (docs/12, SAI-1344).
 * Deliberately a summary, not the order: nothing here is a line the engine could act on,
 * and the full order is fetched once a row is picked.
 */
export const OrderSummarySchema = z.object({
  orderRef: z.string().min(1),
  placedAt: IsoTimestampSchema.nullable(),
  deliveredAt: IsoTimestampSchema.nullable(),
  status: OrderStatusSchema,
  /** The store's own wording for the status, both languages (may be the same string). */
  statusLabel: LocalizedTextSchema,
  totalHalalas: MoneyHalalasSchema,
  /** `null` when the store's list endpoint does not carry lines (Salla's does not). */
  itemCount: z.number().int().nonnegative().nullable(),
  /** Up to three product names, enough to recognise the order at a glance. May be empty. */
  itemNames: z.array(LocalizedTextSchema).max(3),
});
export type OrderSummary = z.infer<typeof OrderSummarySchema>;
